import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/api/users.api';
import { permissionsApi } from '@/api/permissions.api';
import { useAuth } from '@/context/AuthContext';
import { useToasts } from '@/context/ToastContext';

/**
 * V4.7 — Professional Permissions matrix.
 *
 * Previously every capability was rendered as a single inline checkbox in
 * one giant wrapping paragraph — visually unreadable. This rewrite:
 *
 *  - Groups capabilities by domain (Tickets, Users, Shopify, …)
 *  - Each capability gets a friendly label + one-line description
 *  - Toggle-switch UI (Material-style) replaces tiny checkboxes
 *  - Per-group "Allow all / Clear all" bulk actions
 *  - Live filter box to search by capability name or label
 *  - Sticky footer with the Save button always visible, plus an
 *    "unsaved changes" indicator so admins know when the button matters
 *  - Save button stays clickable whenever a user is selected
 */

type CapabilityMeta = {
  capability: string;
  label: string;
  description: string;
};

type CapabilityGroup = {
  id: string;
  title: string;
  icon: string;
  capabilities: CapabilityMeta[];
};

// V4.7 — Friendly metadata keyed by capability string. If a capability
// arrives from the backend that isn't listed here, it falls into the
// "Other" group with the raw key shown.
const CAPABILITY_GROUPS: CapabilityGroup[] = [
  {
    id: 'dashboard',
    title: 'Dashboard & Queues',
    icon: '📊',
    capabilities: [
      { capability: 'dashboard.view', label: 'View dashboard', description: 'See the main dashboard, KPIs, and workload cards.' },
      { capability: 'my_queue.view', label: 'View My Queue', description: 'Access the personal queue of assigned tickets.' },
      { capability: 'reports.view', label: 'View reports', description: 'Access reporting and analytics pages.' }
    ]
  },
  {
    id: 'tickets',
    title: 'Tickets',
    icon: '🎫',
    capabilities: [
      { capability: 'tickets.view', label: 'View tickets', description: 'Read access to the tickets list and ticket detail pages.' },
      { capability: 'tickets.create', label: 'Create tickets', description: 'Raise new tickets via the "New ticket" flow.' },
      { capability: 'tickets.update_status', label: 'Change status', description: 'Move tickets between Open, In Progress, Pending, etc.' },
      { capability: 'tickets.log_cx', label: 'Log CX communications', description: 'Record customer-facing communications on a ticket.' },
      { capability: 'tickets.add_note', label: 'Add notes', description: 'Add public or private internal notes.' },
      { capability: 'tickets.handoff', label: 'Handoff ticket', description: 'Transfer ownership with a handoff note.' },
      { capability: 'tickets.reassign', label: 'Reassign ticket', description: 'Change the owner, solver, or communicator on a ticket.' },
      { capability: 'tickets.close', label: 'Close ticket', description: 'Mark a ticket Resolved or Cancelled.' },
      { capability: 'tickets.approve', label: 'Approve / deny', description: 'Decide on Admin approval requests (B-category, etc.).' },
      { capability: 'tickets.archive', label: 'Archive tickets', description: 'Move resolved tickets out of the active list.' },
      { capability: 'tickets.restore', label: 'Restore tickets', description: 'Bring archived or deleted tickets back into circulation.' },
      { capability: 'tickets.delete', label: 'Delete tickets', description: 'Soft-delete tickets. Admin only by default.' },
      { capability: 'tickets.view_archived', label: 'View archive', description: 'Access the Archived tickets page.' },
      { capability: 'tickets.view_deleted', label: 'View deleted', description: 'Access the Deleted tickets page.' }
    ]
  },
  {
    id: 'approvals',
    title: 'Approvals',
    icon: '✅',
    capabilities: [
      { capability: 'approvals.view', label: 'View approvals', description: 'See the Pending Approvals queue.' }
    ]
  },
  {
    id: 'users',
    title: 'Users',
    icon: '👥',
    capabilities: [
      { capability: 'users.view', label: 'View users', description: 'Open the Users page and see the user directory.' },
      { capability: 'users.create', label: 'Create users', description: 'Add new users to the system.' },
      { capability: 'users.update', label: 'Edit users', description: 'Modify a user’s name, role, store, or category families.' },
      { capability: 'users.reset_pin', label: 'Reset PIN', description: 'Reset another user’s login PIN.' },
      { capability: 'users.delete', label: 'Delete users', description: 'Soft-delete a user account. Cannot delete self or last Admin.' }
    ]
  },
  {
    id: 'permissions',
    title: 'Permissions',
    icon: '🔐',
    capabilities: [
      { capability: 'permissions.manage', label: 'Manage permissions', description: 'Edit this matrix for other users. Admin-level access.' }
    ]
  },
  {
    id: 'shopify',
    title: 'Shopify',
    icon: '🛍️',
    capabilities: [
      { capability: 'shopify.view', label: 'View Shopify data', description: 'See Shopify orders, drafts, and customer lookups.' },
      { capability: 'shopify.manage', label: 'Manage Shopify', description: 'Configure stores, credentials, and webhooks.' }
    ]
  },
  {
    id: 'attachments',
    title: 'Attachments',
    icon: '📎',
    capabilities: [
      { capability: 'attachments.upload', label: 'Upload files', description: 'Attach files (up to 1 GB) to tickets.' },
      { capability: 'attachments.download', label: 'Download files', description: 'Retrieve files attached to tickets.' }
    ]
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: '🔔',
    capabilities: [
      { capability: 'notifications.view', label: 'View notifications', description: 'See the notification log and email history.' },
      { capability: 'notifications.manage', label: 'Manage notifications', description: 'Configure templates, recipients, and resend.' }
    ]
  },
  {
    id: 'auth',
    title: 'Account',
    icon: '🔑',
    capabilities: [
      { capability: 'auth.change_pin', label: 'Change own PIN', description: 'Allow this user to change their own login PIN.' }
    ]
  }
];

function buildCapabilityIndex() {
  const map = new Map<string, { meta: CapabilityMeta; groupId: string }>();
  for (const g of CAPABILITY_GROUPS) {
    for (const c of g.capabilities) {
      map.set(c.capability, { meta: c, groupId: g.id });
    }
  }
  return map;
}

const CAPABILITY_INDEX = buildCapabilityIndex();

export function PermissionsPage() {
  const queryClient = useQueryClient();
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const [userId, setUserId] = useState('');
  const [filter, setFilter] = useState('');
  const permsQuery = useQuery({
    queryKey: ['permissions', userId],
    queryFn: () => permissionsApi.userPermissions(userId),
    enabled: !!userId
  });
  const { user, refreshCapabilities } = useAuth();
  const { push } = useToasts();

  const saveMutation = useMutation({
    mutationFn: (rows: { capability: string; allowed: boolean }[]) =>
      permissionsApi.upsertUserPermissions(userId, rows),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permissions', userId] });
      push({
        kind: 'success',
        title: 'Permissions saved',
        message:
          "Changes apply on the user's next request. They may need to refresh their browser to update sidebar visibility."
      });
      if (user?.sub === userId) {
        void refreshCapabilities();
      }
      setBaseline({ ...local });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Could not save permissions'
      });
    }
  });

  const [local, setLocal] = useState<Record<string, boolean>>({});
  const [baseline, setBaseline] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!permsQuery.data) return;
    const map: Record<string, boolean> = {};
    for (const row of permsQuery.data) map[row.capability] = row.allowed;
    setLocal(map);
    setBaseline(map);
  }, [permsQuery.data]);

  // Build sectioned view from server-returned capabilities, slotting any
  // unknown keys into a synthetic "Other" group at the bottom.
  const groupedView = useMemo(() => {
    const filterLower = filter.trim().toLowerCase();
    const groups: Array<{
      id: string;
      title: string;
      icon: string;
      items: Array<{ capability: string; label: string; description: string; allowed: boolean }>;
    }> = CAPABILITY_GROUPS.map((g) => ({ id: g.id, title: g.title, icon: g.icon, items: [] }));
    const otherGroup = { id: 'other', title: 'Other', icon: '⚙️', items: [] as typeof groups[number]['items'] };

    for (const capability of Object.keys(local)) {
      const allowed = !!local[capability];
      const indexed = CAPABILITY_INDEX.get(capability);
      const item = indexed
        ? { capability, label: indexed.meta.label, description: indexed.meta.description, allowed }
        : { capability, label: capability, description: 'Custom capability — no description available.', allowed };

      if (
        filterLower &&
        !capability.toLowerCase().includes(filterLower) &&
        !item.label.toLowerCase().includes(filterLower)
      ) {
        continue;
      }

      if (indexed) {
        const target = groups.find((g) => g.id === indexed.groupId)!;
        target.items.push(item);
      } else {
        otherGroup.items.push(item);
      }
    }

    return [...groups.filter((g) => g.items.length > 0), ...(otherGroup.items.length ? [otherGroup] : [])];
  }, [local, filter]);

  const totalCount = Object.keys(local).length;
  const enabledCount = Object.values(local).filter(Boolean).length;
  const hasChanges = useMemo(() => {
    const keys = new Set([...Object.keys(local), ...Object.keys(baseline)]);
    for (const k of keys) {
      if ((local[k] ?? false) !== (baseline[k] ?? false)) return true;
    }
    return false;
  }, [local, baseline]);

  const rows = useMemo(
    () => Object.entries(local).map(([capability, allowed]) => ({ capability, allowed })),
    [local]
  );

  const setGroupAll = (groupId: string, value: boolean) => {
    setLocal((prev) => {
      const next = { ...prev };
      const groupCaps =
        groupId === 'other'
          ? Object.keys(prev).filter((c) => !CAPABILITY_INDEX.has(c))
          : (CAPABILITY_GROUPS.find((g) => g.id === groupId)?.capabilities.map((c) => c.capability) ?? []);
      for (const c of groupCaps) {
        if (c in next) next[c] = value;
      }
      return next;
    });
  };

  const setAllGlobal = (value: boolean) => {
    setLocal((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) next[k] = value;
      return next;
    });
  };

  const selectedUser = (usersQuery.data ?? []).find((u) => u.id === userId);

  return (
    <div className="card permissions-card">
      <div className="card-header permissions-header">
        <div>
          <h3 className="card-title">Permissions matrix</h3>
          <div className="text-muted text-small">
            Fine-tune which capabilities are granted to an individual user. Overrides their role defaults.
          </div>
        </div>
        {userId ? (
          <div className="permissions-summary">
            <div className="permissions-summary-count">
              <span className="permissions-summary-strong">{enabledCount}</span>
              <span className="text-muted"> of {totalCount} enabled</span>
            </div>
            {hasChanges ? (
              <span className="badge badge-warning">Unsaved changes</span>
            ) : (
              <span className="text-muted text-small">All changes saved</span>
            )}
          </div>
        ) : null}
      </div>

      <div className="card-body">
        <div className="permissions-controls">
          <div className="form-group" style={{ flex: '1 1 320px', margin: 0 }}>
            <label className="form-label">Select user</label>
            <select
              className="form-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Choose...</option>
              {(usersQuery.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.alias})
                </option>
              ))}
            </select>
          </div>

          {userId ? (
            <>
              <div className="form-group" style={{ flex: '1 1 240px', margin: 0 }}>
                <label className="form-label">Filter</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search by name (e.g. tickets, shopify)..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
              <div className="permissions-bulk">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAllGlobal(true)}>
                  Allow all
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAllGlobal(false)}>
                  Clear all
                </button>
              </div>
            </>
          ) : null}
        </div>

        {!userId ? (
          <div className="permissions-empty">
            <div style={{ fontSize: 32 }}>🔐</div>
            <div className="permissions-empty-title">Select a user to manage their permissions</div>
            <div className="text-muted">Each user inherits defaults from their role. Use this matrix to grant or revoke individual capabilities.</div>
          </div>
        ) : permsQuery.isLoading ? (
          <div className="permissions-empty">
            <div className="text-muted">Loading permissions...</div>
          </div>
        ) : groupedView.length === 0 ? (
          <div className="permissions-empty">
            <div className="text-muted">No capabilities match "{filter}".</div>
          </div>
        ) : (
          <div className="permissions-groups">
            {groupedView.map((group) => {
              const enabled = group.items.filter((i) => i.allowed).length;
              return (
                <div key={group.id} className="permissions-group">
                  <div className="permissions-group-header">
                    <div className="permissions-group-title">
                      <span className="permissions-group-icon" aria-hidden>{group.icon}</span>
                      <span>{group.title}</span>
                      <span className="permissions-group-count">{enabled}/{group.items.length}</span>
                    </div>
                    <div className="permissions-group-actions">
                      <button
                        type="button"
                        className="btn-link-sm"
                        onClick={() => setGroupAll(group.id, true)}
                      >
                        Allow all
                      </button>
                      <span className="text-muted">·</span>
                      <button
                        type="button"
                        className="btn-link-sm"
                        onClick={() => setGroupAll(group.id, false)}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="permissions-grid">
                    {group.items.map((item) => (
                      <label
                        key={item.capability}
                        className={`permissions-row ${item.allowed ? 'is-on' : ''}`}
                      >
                        <div className="permissions-row-text">
                          <div className="permissions-row-label">{item.label}</div>
                          <div className="permissions-row-desc">{item.description}</div>
                          <code className="permissions-row-code">{item.capability}</code>
                        </div>
                        <span className="permissions-toggle" aria-hidden>
                          <input
                            type="checkbox"
                            checked={item.allowed}
                            onChange={(e) =>
                              setLocal((prev) => ({ ...prev, [item.capability]: e.target.checked }))
                            }
                          />
                          <span className="permissions-toggle-track">
                            <span className="permissions-toggle-thumb" />
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {userId ? (
        <div className="permissions-footer">
          <div className="permissions-footer-info">
            {selectedUser ? (
              <>
                Editing permissions for{' '}
                <strong>{selectedUser.name}</strong>{' '}
                <span className="text-muted">({selectedUser.alias})</span>
              </>
            ) : null}
            {hasChanges ? <span className="permissions-footer-changes">· You have unsaved changes</span> : null}
          </div>
          <div className="permissions-footer-actions">
            {hasChanges ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setLocal({ ...baseline })}
                disabled={saveMutation.isPending}
              >
                Discard changes
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(rows)}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save permissions'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
